"use client";

import * as React from "react";
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Loader2, Menu, Sun, Moon, Laptop, type LucideIcon } from "lucide-react";
import { useTheme } from "next-themes";

import { getFilteredNavItems } from "@constructr-sdk/core/config/nav-items";
import { useAppConfigContext } from "@constructr-sdk/core/config/context/app-config-context";
import { OrganizationsSelect } from '@constructr-sdk/modules/organization/components';
import { CopilotPopupComponent } from "@constructr-sdk/modules/aiassistant/presentation/default/components";
import { Button } from '@constructr-sdk/ui/components/button';
import { Sheet, SheetContent, SheetTrigger } from '@constructr-sdk/ui/components/sheet';
import { Navbar, NavbarCenter, NavbarLeft, NavbarRight } from '@constructr-sdk/ui/components/navbar';
import { BrandLogo } from '@constructr-sdk/ui/components/brand-logo';
import { cn } from '@constructr-sdk/ui/lib/utils';
import { useNavigation } from "@constructr-sdk/modules/context/navigation-context";
import { UserSettingsDropdown } from '../sidebar-navigation/sidebar-navigation.layout';
import { ThemeToggle } from '@constructr-sdk/ui/components/theme';
import { User } from "@constructr-sdk/auth";

// ============================================================================
// Icon Types
// ============================================================================
export type Icon = LucideIcon;

export const Icons = {
  sun: Sun,
  moon: Moon,
  laptop: Laptop,
};

// ============================================================================
// Top Navigation Main Component
// ============================================================================
function TopNavMain({ profile }: { profile?: User }): React.JSX.Element {
  const pathname = usePathname();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const { setTheme } = useTheme();
  const { config: appConfig } = useAppConfigContext();

  return (
    <Navbar className="px-4 lg:px-6 w-full h-full">
      <NavbarLeft>
        {/* Mobile Menu Trigger */}
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="lg:hidden">
              <Menu className="h-5 w-5" />
              <span className="sr-only">Toggle menu</span>
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-64">
            <div className="flex flex-col space-y-4 py-4">
              <BrandLogo className="mb-4 h-10" hideWordmark={true} />
              {getFilteredNavItems(appConfig, profile?.role || "user").map((item, index) => (
                <Link
                  key={index}
                  href={item.disabled ? '#' : item.href}
                  target={item.external ? '_blank' : undefined}
                  className={cn(
                    'flex items-center px-2 py-1 text-sm font-medium transition-colors hover:text-accent-foreground',
                    pathname.startsWith(item.href)
                      ? 'text-foreground'
                      : 'text-muted-foreground'
                  )}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.icon && <item.icon className="mr-2 h-4 w-4" />}
                  {item.icon_url && !item.icon && <img src={item.icon_url} className="mr-2 h-4 w-4" />}
                  {item.title}
                </Link>
              ))}
            </div>
          </SheetContent>
        </Sheet>

        {/* Desktop Logo */}
        <div className="hidden lg:block">
          <BrandLogo className="h-10" />
        </div>
        {appConfig?.capabilityFlags?.organization?.enabled && <div className="mx-2">
          <OrganizationsSelect />
        </div>}
      </NavbarLeft>

      <NavbarCenter>

      </NavbarCenter>

      <NavbarRight>
        {/* Desktop Menu */}
        <div className="hidden space-x-4 lg:flex">
          {getFilteredNavItems(appConfig, profile?.role || "user").map((item, index) => (
            <Link
              key={index}
              href={item.disabled ? '#' : item.href}
              target={item.external ? '_blank' : undefined}
              className={cn(
                'inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                pathname.startsWith(item.href)
                  ? 'bg-accent text-accent-foreground'
                  : 'text-muted-foreground'
              )}
            >
              {item.icon &&
                <item.icon className="mr-2 h-4 w-4" />}
              {item.icon_url && !item.icon && <img src={item.icon_url} className="mr-2 h-4 w-4" />}
              {item.title}
            </Link>
          ))}
        </div>

        {/* Theme Selector */}
        <ThemeToggle />

        <UserSettingsDropdown
          profile={profile}
          trigger={
            profile && (
              <Button variant="outline" size="icon" className="rounded-full">
                <UserSettingsDropdown.Avatar profile={profile} />
              </Button>
            )
          }
        />
      </NavbarRight>
    </Navbar>
  );
}

// ============================================================================
// Top Navigation Layout
// ============================================================================
export default function TopNavigationLayout({
  children,
}: React.PropsWithChildren): React.JSX.Element {
  const { isLoading, showCopilot, activeOrganizationId, userDto } = useNavigation();

  return (
    <div className="flex h-screen flex-col w-full">
      <header className="sticky top-0 z-50 h-16 shrink-0 border-b border-border bg-background/80 backdrop-blur-lg">
        <TopNavMain profile={userDto} />
      </header>

      <main className="flex-1 w-full min-h-0 mx-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
            </div>
          </div>
        ) : (
          children
        )}
      </main>
      {showCopilot && <CopilotPopupComponent userId={userDto?.id} organizationId={activeOrganizationId} />}
    </div >
  );
}
